import { useState, useEffect } from 'react';
import { Bell, Plus, Mail, Calendar, Settings, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn, formatDate } from '@/lib/utils';
import type { Notification } from '@/types';
import { api } from '@/lib/api';

interface ReminderRule {
  id: string;
  name: string;
  trigger: string;
  days_before?: number;
  template?: string;
  is_active: boolean;
}

interface NotificationSetting {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
}

export function NotificationsAdminPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [reminderRules, setReminderRules] = useState<ReminderRule[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<NotificationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderForm, setReminderForm] = useState({ name: '', trigger: 'activity_due', days_before: 1 });
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | Notification['type']>('all');
  const [rulesAvailable, setRulesAvailable] = useState(false);
  const [settingsAvailable, setSettingsAvailable] = useState(false);
  const [savingSetting, setSavingSetting] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async <T,>(path: string) => {
      const response = await api.get<{ success?: boolean; data?: T[]; error?: string }>(path);
      if (response.success === false || !Array.isArray(response.data)) throw new Error(response.error || 'Daten konnten nicht geladen werden.');
      return response.data;
    };
    Promise.allSettled([
      load<Notification>('/notifications'),
      load<ReminderRule>('/admin/reminder-rules'),
      load<NotificationSetting>('/admin/notification-settings'),
    ]).then(([notifications, rules, settings]) => {
      if (cancelled) return;
      if (notifications.status === 'fulfilled') setNotifications(notifications.value);
      else setError('Benachrichtigungen konnten nicht geladen werden.');
      setRulesAvailable(rules.status === 'fulfilled');
      if (rules.status === 'fulfilled') setReminderRules(rules.value);
      setSettingsAvailable(settings.status === 'fulfilled');
      if (settings.status === 'fulfilled') setNotificationSettings(settings.value);
    })
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const toggleSetting = async (key: string, enabled: boolean) => {
    if (savingSetting) return;
    setSavingSetting(key);
    try {
      const result = await api.patch<{ success?: boolean; error?: string }>('/admin/notification-settings', { key, enabled });
      if (result.success === false) throw new Error(result.error || 'Einstellung konnte nicht gespeichert werden.');
      setNotificationSettings((prev) => prev.map((s) => (s.key === key ? { ...s, enabled } : s)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Einstellung konnte nicht gespeichert werden.');
    } finally {
      setSavingSetting(null);
    }
  };

  const saveReminder = async () => {
    try {
      await api.post('/admin/reminder-rules', reminderForm);
      setReminderDialogOpen(false);
      setReminderForm({ name: '', trigger: 'activity_due', days_before: 1 });
      const res = await api.get<{ data?: ReminderRule[] }>('/admin/reminder-rules');
      setReminderRules((res as { data?: ReminderRule[] }).data ?? reminderRules);
    } catch (e) {
      setError((e as Error).message ?? 'Erinnerungsregel konnte nicht gespeichert werden.');
    }
  };

  const typeLabel: Record<Notification['type'], string> = {
    reminder: 'Erinnerung',
    assignment: 'Zuweisung',
    status_change: 'Status',
    mention: 'Erwähnung',
    system: 'System',
    deadline: 'Frist',
    review: 'Freigabe',
  };

  const filteredNotifications = notifications.filter((notification) => {
    const matchesSearch = !search || notification.title.toLowerCase().includes(search.toLowerCase()) || (notification.message ?? '').toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || notification.type === typeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-4xl space-y-2">
        <h1 className="text-2xl font-bold text-foreground">Benachrichtigungen & Erinnerungen</h1>
        <p className="text-sm leading-relaxed text-muted">
          Zentrale Regeln für Ereignisse und Erinnerungen (Wer wird bei Budget, Deadlines, Content-Freigaben informiert?).
          Abstimmung mit dem Team vermeidet doppelte oder fehlende Hinweise. Nutzer können eigene Kanäle im Profil dämpfen.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Benachrichtigungen suchen..."
          className="max-w-sm"
        />
        <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as 'all' | Notification['type'])}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Typ filtern" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Typen</SelectItem>
            <SelectItem value="reminder">Erinnerung</SelectItem>
            <SelectItem value="assignment">Zuweisung</SelectItem>
            <SelectItem value="status_change">Status</SelectItem>
            <SelectItem value="mention">Erwähnung</SelectItem>
            <SelectItem value="system">System</SelectItem>
            <SelectItem value="deadline">Frist</SelectItem>
            <SelectItem value="review">Freigabe</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {error && (
        <div role="alert" className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
        </div>
      )}

      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Letzte Benachrichtigungen
          </CardTitle>
          <p className="text-sm text-muted">Übersicht der zuletzt empfangenen Benachrichtigungen.</p>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[280px]">
            <ul className="space-y-2">
              {filteredNotifications.length === 0 ? (
                <li className="py-8 text-center text-muted">{error ? 'Benachrichtigungen nicht verfügbar.' : 'Keine Benachrichtigungen gefunden.'}</li>
              ) : (
                filteredNotifications.map((n) => (
                  <li
                    key={n.id}
                    className={cn(
                      'flex items-start justify-between gap-4 rounded-lg border px-3 py-2 dark:border-border',
                      !n.is_read && 'border-border bg-card-hover/50 dark:bg-primary-hover/20'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">{typeLabel[n.type]}</Badge>
                        <span className="text-xs text-muted">{formatDate(n.created_at, 'relative')}</span>
                      </div>
                      <p className="font-medium text-foreground mt-0.5">{n.title}</p>
                      {n.message && <p className="text-sm text-muted truncate">{n.message}</p>}
                    </div>
                    {n.sent_via_email && <Mail className="h-4 w-4 shrink-0 text-muted" aria-label="Per E-Mail gesendet" />}
                  </li>
                ))
              )}
            </ul>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Benachrichtigungseinstellungen
          </CardTitle>
          <p className="text-sm text-muted">Welche Ereignisse E-Mail-Benachrichtigungen auslösen.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!settingsAvailable && <p className="text-sm text-muted">Globale Benachrichtigungseinstellungen sind nicht verfügbar.</p>}
          {notificationSettings.map((s) => (
            <div key={s.key} className="flex items-center justify-between rounded-lg border border-border p-4">
              <div>
                <Label className="font-medium">{s.label}</Label>
                <p className="text-sm text-muted">{s.description}</p>
              </div>
              <Switch aria-label={s.label} disabled={savingSetting !== null} checked={s.enabled} onCheckedChange={(v) => void toggleSetting(s.key, v)} />
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Erinnerungsregeln
              </CardTitle>
              <p className="text-sm text-muted mt-1">Automatische Erinnerungen zu Fälligkeiten und Meilensteinen.</p>
            </div>
            <Button size="sm" disabled={!rulesAvailable} onClick={() => setReminderDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Erinnerung anlegen
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {reminderRules.length === 0 ? (
              <li className="py-6 text-center text-muted">{rulesAvailable ? 'Keine Erinnerungsregeln.' : 'Globale Erinnerungsregeln sind nicht verfügbar.'}</li>
            ) : (
              reminderRules.map((r) => (
                <li key={r.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                  <div>
                    <p className="font-medium text-foreground">{r.name}</p>
                    <p className="text-xs text-muted">
                      {r.trigger === 'activity_due' && 'Aktivität fällig'}
                      {r.trigger === 'campaign_review' && 'Kampagnen-Review'}
                      {r.trigger === 'phase_end' && 'Phasenende'}
                      {r.days_before != null && ` · ${r.days_before} Tag(e) vorher`}
                    </p>
                  </div>
                  <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                </li>
              ))
            )}
          </ul>
        </CardContent>
      </Card>

      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Erinnerungsregel</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Bezeichnung</Label>
              <Input value={reminderForm.name} onChange={(e) => setReminderForm((f) => ({ ...f, name: e.target.value }))} placeholder="z. B. Aktivität Fälligkeit" />
            </div>
            <div className="grid gap-2">
              <Label>Auslöser</Label>
              <Select value={reminderForm.trigger} onValueChange={(v) => setReminderForm((f) => ({ ...f, trigger: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activity_due">Aktivität fällig</SelectItem>
                  <SelectItem value="campaign_review">Kampagnen-Review</SelectItem>
                  <SelectItem value="phase_end">Phasenende</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Tage vorher</Label>
              <Input type="number" min={0} value={reminderForm.days_before} onChange={(e) => setReminderForm((f) => ({ ...f, days_before: Number(e.target.value) || 0 }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={saveReminder}>
              <Plus className="h-4 w-4" />
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
